import type { ProductContext } from "../types";
import { extractSlugFromUrl, extractSlugPathFromUrl } from "./json-ld";

const PRODUCT_TYPE = /schema\.org\/Product$/i;
const GTIN_PROPS = ["gtin14", "gtin13", "gtin12", "gtin8", "gtin"];

// Primeiro itemscope do tipo Product da página. Na PDP do Shopware 5 é o único (o
// produto principal vem antes de qualquer outro bloco no DOM).
function findProductScope(): Element | null {
  const scopes = Array.from(document.querySelectorAll("[itemscope][itemtype]"));
  return scopes.find((el) => PRODUCT_TYPE.test(el.getAttribute("itemtype") ?? "")) ?? null;
}

// Lê só os itemprops que pertencem ao próprio nó Product. Sem esse filtro, um "sku" de
// Offer ou Brand aninhado poderia ganhar do "sku" do produto.
function readOwnItemprop(scope: Element, prop: string): string | null {
  const target = prop.toLowerCase();
  const el = Array.from(scope.querySelectorAll("[itemprop]")).find(
    (node) =>
      node.getAttribute("itemprop")?.toLowerCase() === target &&
      node.closest("[itemscope]") === scope,
  );
  if (!el) return null;

  const value = el.getAttribute("content") ?? el.getAttribute("href") ?? el.textContent ?? "";
  return value.trim() || null;
}

// Microdata (itemscope/itemprop) é o que o Shopware 5 publica na PDP, no lugar de JSON-LD.
// Itemprops são case-sensitive no HTML mas o schema.org não: o Shopware usa "MPN" e "sku",
// por isso comparamos em minúsculo. A URL vem do og:url, que o tema sempre preenche.
export function findProductMicrodata(): ProductContext | null {
  const scope = findProductScope();
  if (!scope) return null;

  const url =
    readOwnItemprop(scope, "url") ??
    document.querySelector('meta[property="og:url"]')?.getAttribute("content") ??
    document.querySelector('link[rel="canonical"]')?.getAttribute("href") ??
    window.location.href;

  return {
    productId: null,
    sku: readOwnItemprop(scope, "sku") ?? readOwnItemprop(scope, "mpn"),
    gtin: GTIN_PROPS.map((prop) => readOwnItemprop(scope, prop)).find(Boolean) ?? null,
    slug: extractSlugFromUrl(url),
    slugPath: extractSlugPathFromUrl(url),
  };
}
